/** @jsx jsx */
import { Flex, Grid, Heading, Paragraph } from "@eworkit/iris";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { CollapsibleArea, CollapsibleButton, useCollapsible } from "@/components/collapsible";
import { ResponsiveCard } from "@/components/ResponsiveCard";
import { JobApplication } from "@/data/job-applications/types/JobApplication";
import { getJobRequestClientInfo } from "@/data/job-requests";
import { formatFullName } from "@/data/persons";
import { useFormats } from "@/hooks/Formats";

interface JobApplicationDetailsGeneralInformationProps {
  jobApplication: JobApplication;
}

export const JobApplicationDetailsGeneralInformation: FC<JobApplicationDetailsGeneralInformationProps> = ({
  jobApplication,
}) => {
  const { t } = useTranslation("session");
  const { formatDate, formatDateTime } = useFormats();
  const collapsibleState = useCollapsible(false);
  const clientInfo = jobApplication.jobRequest ? getJobRequestClientInfo(jobApplication.jobRequest) : null;

  return (
    <ResponsiveCard sx={{ py: ["sm", "sm", "sm", "md"] }}>
      <Grid gap="sm">
        <Flex justifyContent="space-between" alignItems="center">
          <Heading size="h-sm">{t("JOB_APPLICATIONS.DETAIL.GENERAL.SECTION_HEADER")}</Heading>
          <CollapsibleButton {...collapsibleState} />
        </Flex>

        <CollapsibleArea {...collapsibleState}>
          <Grid>
            <Grid columns={["1fr", null, "1fr 1fr"]} gap={["xxs", null, "sm"]}>
              <React.Fragment>
                <Paragraph color="n700">{t("JOB_APPLICATIONS.DETAIL.GENERAL.CLIENT")}</Paragraph>
                <Paragraph data-test="general-clientName">{clientInfo?.name}</Paragraph>
              </React.Fragment>
            </Grid>
            <Grid columns={["1fr", null, "1fr 1fr"]} gap={["xxs", null, "sm"]}>
              <Paragraph color="n700">{t("JOB_APPLICATIONS.DETAIL.GENERAL.ASSIGNMENT_PERIOD")}</Paragraph>
              <Paragraph data-test="general-assignmentPeriod">
                {formatDate(jobApplication?.jobRequest?.startDate)}
                <br />
                {formatDate(jobApplication?.jobRequest?.endDate)}
              </Paragraph>
            </Grid>

            {jobApplication.submittedDate && (
              <Grid columns={["1fr", null, "1fr 1fr"]} gap={["xxs", null, "sm"]}>
                <Paragraph color="n700">{t("JOB_APPLICATIONS.DETAIL.GENERAL.SUBMITTED_DATE")}</Paragraph>
                <Paragraph data-test="general-submittedOn">{formatDateTime(jobApplication.submittedDate)}</Paragraph>
              </Grid>
            )}

            {jobApplication.submittedByDto && (
              <Grid columns={["1fr", null, "1fr 1fr"]} gap={["xxs", null, "sm"]}>
                <Paragraph color="n700">{t("JOB_APPLICATIONS.DETAIL.GENERAL.SUBMITTED_BY")}</Paragraph>
                <Paragraph data-test="general-submittedBy">{formatFullName(jobApplication.submittedByDto)}</Paragraph>
              </Grid>
            )}
          </Grid>
        </CollapsibleArea>
      </Grid>
    </ResponsiveCard>
  );
};
